/// Firestore-backed user profile.
///
/// One document per UID at `webUsers/{uid}`. Fields:
///
///   - createdAt / lastSeenAt — millis epoch (client clock)
///   - authProvider — 'anonymous' | 'email' | 'google' | 'apple' (Apple = forward-compat)
///   - lastViewedLevel — id of the most-recently-played level; the
///     home screen highlights it, the radar can resume it
///   - levelStats — map of levelId → { bestScore, bestSuccessPct,
///     playCount, lastPlayedAt } recorded at end-of-level
///
/// Stored as a single doc (no sub-collections) since the data is
/// small (≤ ~50 levels × ~5 fields = a few KB) and the app reads
/// the whole profile at boot. Switch to sub-collections only if we
/// ever exceed Firestore's 1 MB doc cap.

import {
  doc,
  getDoc,
  serverTimestamp,
  setDoc,
  updateDoc,
} from 'firebase/firestore'
import { firestore } from '../firebase/app'
import { nextDailyState, type DailyChallengeState } from './towerHome'

/// The four difficulty tiers iOS publishes per level. Web's URL
/// `?difficulty=` accepts these names; `Trainee` is the default if
/// nothing is specified.
export type DifficultyName = 'Novice' | 'Trainee' | 'Licensed' | 'Master'

export const DIFFICULTY_ORDER: readonly DifficultyName[] = [
  'Novice',
  'Trainee',
  'Licensed',
  'Master',
] as const

export interface CompletedStat {
  /// Wall-clock time of the run that completed the level (ms epoch).
  completedAt: number
  /// Session score that achieved completion. Per-difficulty bests
  /// can be derived from this map by taking the max across entries.
  score: number
}

export interface LevelStats {
  bestScore: number
  bestSuccessPct: number
  playCount: number
  lastPlayedAt: number
  /// Per-difficulty completion records. A level is "completed at X"
  /// when a session at difficulty X ended with success === true.
  /// Absent ⟹ never completed at that difficulty.
  completed?: Partial<Record<DifficultyName, CompletedStat>>
}

/// Highest tier completed for a given LevelStats record (or null if
/// none). Used by the home-screen pin color to reflect progression.
export function highestCompletedDifficulty(
  stats: LevelStats | undefined,
): DifficultyName | null {
  if (!stats?.completed) return null
  for (let i = DIFFICULTY_ORDER.length - 1; i >= 0; i--) {
    const d = DIFFICULTY_ORDER[i]!
    if (stats.completed[d]) return d
  }
  return null
}

export type AuthProvider = 'anonymous' | 'apple' | 'email' | 'google'

export interface UserProfile {
  uid: string
  authProvider: AuthProvider
  createdAt: number
  lastSeenAt: number
  lastViewedLevel: string
  levelStats: Record<string, LevelStats>
  /// Lifetime total — sum of the end-of-level score from every play
  /// the user has finished. Independent of per-level bestScore;
  /// replays of a level that score lower than a previous run still
  /// add to this total. Never decreases (session scores are already
  /// clamped at zero).
  totalScore: number
  /// Daily-challenge streak. Absent until the first daily is completed.
  daily?: DailyChallengeState
}

const userDoc = (uid: string) => doc(firestore, 'webUsers', uid)

/// Load the profile if present; otherwise create a fresh one. Always
/// updates `lastSeenAt`. Idempotent — safe to call on every boot.
///
/// Existing-doc reads are passed through `withProfileDefaults` so any
/// fields added to the schema after a user's first sign-in (e.g.,
/// `totalScore`) get filled in with sensible defaults instead of
/// landing as `undefined`. The doc is then back-filled on Firestore
/// so subsequent reads return a complete record.
export async function loadOrCreateProfile(args: {
  uid: string
  authProvider: AuthProvider
  defaultLastViewedLevel: string
}): Promise<UserProfile> {
  const ref = userDoc(args.uid)
  const snap = await getDoc(ref)
  if (snap.exists()) {
    const raw = snap.data() as Partial<UserProfile>
    const filled = withProfileDefaults(raw, args)
    const patch: Record<string, unknown> = {
      lastSeenAt: Date.now(),
      _serverSeenAt: serverTimestamp(),
    }
    // Back-fill any missing schema fields so future reads + writes
    // don't have to keep defaulting.
    if (raw.totalScore == null) patch.totalScore = filled.totalScore
    if (raw.levelStats == null) patch.levelStats = filled.levelStats
    if (raw.lastViewedLevel == null) patch.lastViewedLevel = filled.lastViewedLevel
    if (raw.authProvider == null) patch.authProvider = filled.authProvider
    if (raw.createdAt == null) patch.createdAt = filled.createdAt
    await updateDoc(ref, patch)
    return filled
  }
  const profile: UserProfile = {
    uid: args.uid,
    authProvider: args.authProvider,
    createdAt: Date.now(),
    lastSeenAt: Date.now(),
    lastViewedLevel: args.defaultLastViewedLevel,
    levelStats: {},
    totalScore: 0,
  }
  await setDoc(ref, { ...profile, _serverCreatedAt: serverTimestamp() })
  return profile
}

function withProfileDefaults(
  raw: Partial<UserProfile>,
  args: { uid: string; authProvider: AuthProvider; defaultLastViewedLevel: string },
): UserProfile {
  return {
    uid: raw.uid ?? args.uid,
    authProvider: raw.authProvider ?? args.authProvider,
    createdAt: raw.createdAt ?? Date.now(),
    lastSeenAt: Date.now(),
    lastViewedLevel: raw.lastViewedLevel ?? args.defaultLastViewedLevel,
    levelStats: raw.levelStats ?? {},
    totalScore: raw.totalScore ?? 0,
    daily: raw.daily,
  }
}

/// Advance the daily-challenge streak after completing today's daily level.
/// Writes the `daily` field on the same webUsers doc the profile already owns
/// (no rule change). Returns the new state (unchanged object if already done
/// today, so the caller can skip a redundant local update).
export async function recordDailyCompletion(args: {
  uid: string
  today: string
  yesterday: string
  score: number
  difficulty: string
  profile: UserProfile
}): Promise<DailyChallengeState> {
  const prev = args.profile.daily
  const streak = nextDailyState(prev, args.today, args.yesterday)
  const sameDay = prev?.lastDate === args.today
  const prevScore = sameDay ? (prev?.lastScore ?? 0) : 0
  const lastScore = Math.max(prevScore, args.score)
  const lastDifficulty =
    lastScore === args.score ? args.difficulty : (prev?.lastDifficulty ?? args.difficulty)
  const next: DailyChallengeState = {
    lastDate: streak.lastDate,
    streak: streak.streak,
    best: streak.best,
    lastScore,
    lastDifficulty,
  }
  await updateDoc(userDoc(args.uid), {
    daily: next,
    lastSeenAt: Date.now(),
    _serverSeenAt: serverTimestamp(),
  })
  return next
}

export async function setLastViewedLevel(uid: string, levelId: string): Promise<void> {
  await updateDoc(userDoc(uid), {
    lastViewedLevel: levelId,
    lastSeenAt: Date.now(),
    _serverSeenAt: serverTimestamp(),
  })
}

/// Stamp the profile doc with the current auth provider — called
/// after a successful anonymous→Google upgrade so subsequent boots
/// reflect the right tag without a re-read.
export async function setAuthProvider(uid: string, provider: AuthProvider): Promise<void> {
  await updateDoc(userDoc(uid), {
    authProvider: provider,
    lastSeenAt: Date.now(),
    _serverSeenAt: serverTimestamp(),
  })
}

export interface RecordLevelEndResult {
  stats: LevelStats
  /// New lifetime total after adding `score` to the previous total.
  totalScore: number
}

/// Fold an end-of-level outcome into the profile's level-stats map
/// AND the lifetime totalScore. `bestScore` and `bestSuccessPct`
/// retain the all-time max per level; play count + lastPlayedAt
/// update unconditionally; totalScore += this run's score; on a
/// successful run, the per-difficulty `completed` slot is recorded
/// (or kept at the higher of the two scores if already present).
export async function recordLevelEnd(args: {
  uid: string
  levelId: string
  difficulty: DifficultyName
  /// Whether the session's level-end state was `finished: success`.
  succeeded: boolean
  score: number
  successCount: number
  totalHandled: number
  /// Pass the cached profile so we can compute the new "best" without
  /// a round-trip read. Fall back to in-Firestore read if not given.
  profile?: UserProfile
}): Promise<RecordLevelEndResult> {
  const ref = userDoc(args.uid)
  let existing = args.profile?.levelStats?.[args.levelId]
  let priorTotal = args.profile?.totalScore ?? 0
  if (!args.profile) {
    const snap = await getDoc(ref)
    const data = snap.data() as UserProfile | undefined
    existing = data?.levelStats?.[args.levelId]
    priorTotal = data?.totalScore ?? 0
  }
  const successPct = args.totalHandled === 0 ? 0 : args.successCount / args.totalHandled
  const completed = { ...(existing?.completed ?? {}) }
  if (args.succeeded) {
    const prior = completed[args.difficulty]
    completed[args.difficulty] = {
      completedAt: Date.now(),
      score: Math.max(prior?.score ?? 0, args.score),
    }
  }
  const stats: LevelStats = {
    bestScore: Math.max(args.score, existing?.bestScore ?? 0),
    bestSuccessPct: Math.max(successPct, existing?.bestSuccessPct ?? 0),
    playCount: (existing?.playCount ?? 0) + 1,
    lastPlayedAt: Date.now(),
    completed,
  }
  const totalScore = priorTotal + Math.max(0, args.score)
  await updateDoc(ref, {
    [`levelStats.${args.levelId}`]: stats,
    totalScore,
    lastSeenAt: Date.now(),
    _serverSeenAt: serverTimestamp(),
  })
  return { stats, totalScore }
}
